import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Platform } from 'react-native';

import { LAUNCH_PHASE } from '../constants/launchDiagnostics.constants';
import { NATIVE_SDK_BRIDGE_MIN_MS } from '../constants/nativeSdkInit.constants';
import { logLaunchPhase } from '../helpers/launchDiagnostics.helper';

export interface NativeSdkInitState {
  bridgeReady: boolean;
  analyticsReady: boolean;
  crashReportingReady: boolean;
}

const ALL_READY: NativeSdkInitState = {
  bridgeReady: true,
  analyticsReady: true,
  crashReportingReady: true,
};

const ALL_BLOCKED: NativeSdkInitState = {
  bridgeReady: false,
  analyticsReady: false,
  crashReportingReady: false,
};

const NativeSdkInitContext = createContext<NativeSdkInitState>(ALL_READY);

export function NativeSdkInitProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NativeSdkInitState>(
    Platform.OS === 'ios' ? ALL_BLOCKED : ALL_READY,
  );

  useEffect(() => {
    void logLaunchPhase(LAUNCH_PHASE.PROVIDER_MOUNT);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    let cancelled = false;

    const minTimer = setTimeout(() => {
      void logLaunchPhase(LAUNCH_PHASE.DEFERRED_SDK_READY, 'bridge').then(() => {
        if (cancelled) return;
        setState(ALL_READY);
      });
    }, NATIVE_SDK_BRIDGE_MIN_MS);

    return () => {
      cancelled = true;
      clearTimeout(minTimer);
    };
  }, []);

  return (
    <NativeSdkInitContext.Provider value={state}>{children}</NativeSdkInitContext.Provider>
  );
}

export function useNativeSdkInit(): NativeSdkInitState {
  return useContext(NativeSdkInitContext);
}
